(ns ui.components.card.kanban
  "The card of a Kanban board. It follows the kanban variant of the ZapKit Card.
   The class names come from the generated `ui.components.card.zapkit-styles`.
   `dom-overrides` has the classes that ZapKit does not have."
  (:require
   [common.localization :as loc]
   [ui.components.card.zapkit-styles :as styles]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.dom-globals :as dg]
   [ui.icons :as icons]
   [ui.util :as uu]))

(def dom-overrides
  "Classes that ZapKit does not have. Each key names the upstream key that it
   extends. Add a class here. Do not edit a generated class string.

   `:selected` extends `[:compound :kanban :interactive]`. ZapKit has no
   selected card.
   `:title-row` extends `[:kanban :header :title-row]`. The row fills the
   header, so a click next to the title has the same effect as a click on the
   title.
   `:title` extends `[:kanban :header :title]`. A long title breaks inside the
   column.
   `:menu-trigger` extends `[:kanban :header :menu-trigger]`. The ZapKit trigger
   is 12 px wide. This trigger has a hit area of 20 px and a focus outline. The
   negative margin keeps the layout of the header."
  {:selected     "tw2-border-card-border-active tw2-bg-card-bg-active"
   :title-row    "tw2-min-w-0 tw2-flex-1"
   :title        "tw2-min-w-0 tw2-break-words"
   :menu-trigger "tw2-inline-flex tw2-size-5 -tw2-m-1 tw2-shrink-0 tw2-items-center tw2-justify-center focus-visible:tw2-outline focus-visible:tw2-outline-2"})

(def kanban-styles
  (:kanban styles/card))

(def known-statuses
  #{:won :on-track :lost})

(defn status-bar-class
  "Return the classes of the status bar of a card, or nil for the status
   `:no-status`. A status that the card does not know has the bar of `:won`,
   the default."
  [status]
  (when-not (= :no-status status)
    (str (get-in kanban-styles [:status-bar :container])
         " "
         (get-in kanban-styles [:status-bar (get known-statuses status :won)]))))

(defn card-class
  "Return the classes of the card. ZapKit puts the base, the kanban modifiers
   and the interactive state in this order. `uu/cm` resolves the conflicts as
   the `cn` function of ZapKit does."
  [{:keys [selected? full-width?]}]
  (uu/cm (:base styles/card)
         (:modifiers kanban-styles)
         (get-in styles/card [:compound :kanban :interactive])
         (when selected?
           (:selected dom-overrides))
         (when full-width?
           "tw2-w-full")))

(defn menu
  "The menu of the actions of a card. `items` is a vector of menu items, see
   `ui.components.radix-ui.dropdown-menu.view`."
  [{:keys [items]}]
  [:div
   {:class           "tw2-flex tw2-shrink-0 tw2-items-center"
    ;; React events bubble out of the portal of the menu. Without this stop,
    ;; a click on a menu item would also select the card, and a double click
    ;; would open it.
    :on-click        dg/stop-propagation
    :on-double-click dg/stop-propagation}
   [dropdown-menu/view
    {:trigger-as-child [:button
                        {:type       "button"
                         :aria-label (loc/tr :actions)
                         :class      (uu/cm (get-in kanban-styles [:header :menu-trigger])
                                            (:menu-trigger dom-overrides))}
                        [icons/ellipsis-vertical-icon
                         {:class       (get-in kanban-styles [:footer :icon])
                          :aria-hidden true}]]
     :items            items
     :props            {:align "end"}}]])

(defn rows-view
  "The data rows of a card. A row has a label and a value. `:show-labels?` false
   shows the value only."
  [{:keys [rows show-labels?]}]
  (let [row-styles (:data-attribute kanban-styles)]
    [:div {:class (:list row-styles)}
     (for [row rows]
       ^{:key (str "row-" (:key row))}
       [:div {:class (:row row-styles)}
        (when show-labels?
          [:p {:class (:label row-styles)}
           (or (:label row) "")])
        [:p {:class (:value row-styles)}
         (:value row)]])]))

(defn footer-view
  "The footer of a card. `:date?` true puts a calendar icon before the text.
   `:title` is the tooltip of the footer."
  [{:keys [text title date?]}]
  (let [footer-styles (:footer kanban-styles)]
    [:div {:class (:row footer-styles)}
     [:div
      {:class (:meta footer-styles)
       :title title}
      (when date?
        [icons/calendar-icon
         {:class       (:icon footer-styles)
          :aria-hidden true}])
      [:p {:class (:meta-text footer-styles)}
       text]]]))

(defn view
  "Render a card of a Kanban board.

   `:title` is the text of the header.
   `:status` is `:won`, `:on-track`, `:lost` or `:no-status`. The default is the
   bar of `:won`.
   `:rows` is a vector of maps with `:key`, `:label` and `:value`.
   `:show-labels?` false hides the labels of the rows.
   `:footer` is a map with `:text`, `:title` and `:date?`, or nil for no footer.
   `:menu-items` is a vector of menu items. The card has no menu without items.
   `:selected?` and `:collapsed?` set the state of the card. A collapsed card
   has the header only.
   `:full-width?` fills the width of the parent. A card that has no `:width`
   also fills it.
   `:card-id` is the value of the attribute `data-card-id`.
   `:on-click` and `:on-double-click` handle the events of the card.
   `:on-title-click` handles a click on the header."
  [{:keys [title status rows show-labels? footer menu-items selected? collapsed?
           full-width? width card-id on-click on-double-click on-title-click]}]
  (let [bar-class (status-bar-class status)
        full-width? (or full-width?
                        (nil? width))
        header-styles (:header kanban-styles)
        content-styles (:content kanban-styles)]
    [:div
     {:class           (card-class {:selected?   selected?
                                    :full-width? full-width?})
      :style           (when-not full-width?
                         {:width width})
      :data-card-id    (some-> card-id str)
      :on-click        on-click
      :on-double-click on-double-click}
     (when bar-class
       [:div {:class bar-class}])
     [:div
      {:class (uu/cm (:base content-styles)
                     (when-not bar-class
                       (:no-status-bar content-styles)))}
      [:div {:class (:row header-styles)}
       [:div
        {:class    (uu/cm (:title-row header-styles)
                          (:title-row dom-overrides))
         :on-click on-title-click}
        [:p {:class (uu/cm (:title header-styles)
                           (:title dom-overrides))}
         title]]
       (when (seq menu-items)
         [menu {:items menu-items}])]
      (when (and (not collapsed?)
                 (seq rows))
        [rows-view {:rows         rows
                    :show-labels? show-labels?}])
      (when (and (not collapsed?)
                 footer)
        [footer-view footer])]]))
